import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, RefreshCw } from 'lucide-react';
import { FABRIC_CAPTURE_THRESHOLDS } from '../constants/thresholds';
import { HeuristicCornerBackgroundFabricDetector } from '../fabricDetector/heuristicCornerBackgroundFabricDetector';
import { MockFabricDetector } from '../fabricDetector/mockFabricDetector';
import { distanceStateFromCoverage, distanceStateLabel } from '../captureGuidance/distanceFromCoverage';
import { buildGuidanceState, computeCaptureReadiness } from '../captureGuidance/captureReadiness';
import { validateFabricCapture } from '../imageQualityValidator/validateFabricCapture';
import { computeImageQualityScores } from '../imageQualityValidator/imageMetrics';
import { issuesToUserMessages } from '../captureResult/validationMessages';
import { prepareFabricCaptureFile } from '../upload_preparation/prepareFabricCaptureFile';
import { applyZoomLockTo1x } from './applyZoomLock';
import { videoFrameToImageData } from './videoFrameToImageData';

const BOTTOM_TIPS = ['Keep phone parallel to fabric', 'Avoid shadows', 'Do not use zoom'];

/**
 * @typedef {Object} FabricCaptureGuardrailsModalProps
 * @property {MediaStream | null} stream
 * @property {() => void} onClose
 * @property {(file: File) => void | Promise<void>} onValidatedCapture
 * @property {(msg: string, type?: string) => void} [showNotification]
 * @property {'heuristic' | 'mock'} [detectorMode] mock = demo wiring without real scene
 * @property {import('../fabricDetector/FabricDetector').FabricDetector} [detectorOverride]
 */

/**
 * Guided fabric capture with live framing, distance hints, and post-capture validation.
 * @param {FabricCaptureGuardrailsModalProps} props
 */
const FabricCaptureGuardrailsModal = ({
  stream,
  onClose,
  onValidatedCapture,
  showNotification,
  detectorMode = 'heuristic',
  detectorOverride,
}) => {
  const videoRef = useRef(null);
  const canvasFullRef = useRef(null);
  const rafRef = useRef(0);
  const lastTickRef = useRef(0);

  const detectorRef = useRef(detectorOverride || null);
  if (!detectorRef.current) {
    detectorRef.current =
      detectorOverride ||
      (detectorMode === 'mock' ? new MockFabricDetector() : new HeuristicCornerBackgroundFabricDetector());
  }

  const [distanceState, setDistanceState] = useState(/** @type {import('../types').DistanceState} */ ('adjust'));
  const [readiness, setReadiness] = useState(/** @type {import('../types').CaptureReadinessState} */ ('not_ready'));
  const [guidanceHint, setGuidanceHint] = useState('');
  const [retakeMessages, setRetakeMessages] = useState(/** @type {string[] | null} */ (null));
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !stream) return;
    v.srcObject = stream;
    v.play().catch(() => {});
    applyZoomLockTo1x(stream).catch(() => {});
  }, [stream]);

  const tick = useCallback(() => {
    const video = videoRef.current;
    if (!video || retakeMessages) return;
    const now = performance.now();
    if (now - lastTickRef.current < 240) return;
    lastTickRef.current = now;

    const imageData = videoFrameToImageData(video, FABRIC_CAPTURE_THRESHOLDS.analysisMaxWidth);
    if (!imageData) return;

    const det = detectorRef.current;
    if (!det) return;
    det.detectFromImageData(imageData).then((fabricMetrics) => {
      const quality = computeImageQualityScores(imageData);
      const dist = distanceStateFromCoverage(fabricMetrics.coverageRatio);
      const ready = computeCaptureReadiness(fabricMetrics, quality);
      const g = buildGuidanceState(ready, dist);
      setDistanceState(dist);
      setReadiness(ready);
      setGuidanceHint(g.hint || '');
    });
  }, [retakeMessages]);

  useEffect(() => {
    const loop = () => {
      rafRef.current = requestAnimationFrame(loop);
      tick();
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [tick]);

  const handleCapture = async () => {
    const video = videoRef.current;
    const canvas = canvasFullRef.current;
    if (!video || !canvas || !stream) return;
    if (video.readyState < video.HAVE_ENOUGH_DATA || !video.videoWidth) {
      showNotification?.('Camera is not ready. Please wait a moment.', 'error');
      return;
    }

    setIsValidating(true);
    try {
      const tw = video.videoWidth;
      const th = video.videoHeight;
      canvas.width = tw;
      canvas.height = th;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('No canvas context');
      ctx.drawImage(video, 0, 0, tw, th);

      const maxW = FABRIC_CAPTURE_THRESHOLDS.analysisMaxWidth;
      const scale = Math.min(1, maxW / tw);
      const sw = Math.max(1, Math.round(tw * scale));
      const sh = Math.max(1, Math.round(th * scale));
      const sc = document.createElement('canvas');
      sc.width = sw;
      sc.height = sh;
      const sctx = sc.getContext('2d');
      if (!sctx) throw new Error('No small canvas');
      sctx.drawImage(canvas, 0, 0, sw, sh);
      const imageData = sctx.getImageData(0, 0, sw, sh);

      const result = await validateFabricCapture(imageData, detectorRef.current);
      if (!result.ok) {
        setRetakeMessages(issuesToUserMessages(result.issues));
        return;
      }

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/jpeg', 0.92);
      });
      const file = new File([blob], `fabric-capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
      const prepared = await prepareFabricCaptureFile(file);
      await onValidatedCapture(prepared);
    } catch (e) {
      console.error(e);
      showNotification?.('Could not validate or save capture. Try again.', 'error');
    } finally {
      setIsValidating(false);
    }
  };

  const readinessColor =
    readiness === 'ready' ? 'bg-emerald-500' : readiness === 'almost_ready' ? 'bg-amber-400' : 'bg-rose-500';

  const distanceColor =
    distanceState === 'good' ? 'text-emerald-300' : distanceState === 'adjust' ? 'text-amber-200' : 'text-rose-200';

  return (
    <div className="fixed inset-0 z-[10000] flex flex-col bg-black">
      <div className="relative flex flex-1 flex-col min-h-0">
        <div className="absolute left-0 right-0 top-0 z-20 px-4 pt-4 pb-2 bg-gradient-to-b from-black/80 to-transparent">
          <p className="text-center text-sm font-medium text-white/95 tracking-tight">
            Place the full fabric inside the frame
          </p>
          <div className="mt-2 flex items-center justify-center gap-2">
            <span className={`h-2 w-2 rounded-full ${readinessColor} shadow-sm`} aria-hidden />
            <span className="text-xs font-semibold uppercase tracking-wide text-white/80">
              {readiness === 'ready' ? 'Ready to capture' : readiness === 'almost_ready' ? 'Almost ready' : 'Not ready'}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-30 flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-md hover:bg-white/25"
          aria-label="Close camera"
        >
          <X size={22} />
        </button>

        <div
          className="relative flex-1 min-h-0 flex items-center justify-center bg-black touch-none"
          style={{ touchAction: 'none' }}
          onTouchMove={(e) => e.preventDefault()}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            disablePictureInPicture
            className="max-h-full max-w-full object-contain"
          />
          {/* Guide frame overlay */}
          <div className="pointer-events-none absolute inset-6 flex items-center justify-center">
            <div
              className="relative aspect-[3/4] w-[min(88vw,420px)] max-h-[70vh] rounded-2xl border-2 border-white/85 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]"
              aria-hidden
            >
              <div className="absolute -top-8 left-0 right-0 flex justify-center">
                <span className={`rounded-full bg-black/55 px-3 py-1 text-xs font-semibold ${distanceColor}`}>
                  {distanceStateLabel(distanceState)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <canvas ref={canvasFullRef} className="hidden" />

        <div className="relative z-20 bg-gradient-to-t from-black via-black/90 to-transparent px-4 pt-6 pb-8 safe-area-pb">
          {guidanceHint ? (
            <p className="mb-3 text-center text-xs text-white/70">{guidanceHint}</p>
          ) : null}
          <div className="mb-4 flex flex-wrap justify-center gap-x-3 gap-y-1 text-[11px] text-white/55">
            {BOTTOM_TIPS.map((t) => (
              <span key={t}>{t}</span>
            ))}
          </div>
          <div className="flex items-center justify-center gap-6">
            <button
              type="button"
              onClick={onClose}
              className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-md hover:bg-white/25"
              aria-label="Cancel"
            >
              <X size={20} />
            </button>
            <button
              type="button"
              onClick={handleCapture}
              disabled={isValidating}
              className={`relative flex h-[72px] w-[72px] items-center justify-center rounded-full border-4 bg-white transition ${
                readiness === 'ready' ? 'border-emerald-400 shadow-[0_0_24px_rgba(52,211,153,0.45)]' : 'border-slate-300'
              } disabled:opacity-60`}
              aria-label="Capture fabric photo"
            >
              <div className="h-12 w-12 rounded-full border-2 border-slate-400 bg-white" />
              {isValidating ? (
                <span className="absolute inset-0 flex items-center justify-center rounded-full bg-white/80 text-[10px] font-semibold text-slate-700">
                  …
                </span>
              ) : null}
            </button>
            <div className="h-12 w-12" aria-hidden />
          </div>
        </div>
      </div>

      {retakeMessages && (
        <div className="absolute inset-0 z-40 flex items-end justify-center bg-black/75 p-4 sm:items-center">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-slate-900 p-5 shadow-2xl">
            <h3 className="text-lg font-semibold text-white">Adjust and retake</h3>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-slate-200">
              {retakeMessages.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
            <button
              type="button"
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 py-3 text-sm font-semibold text-white hover:bg-indigo-500"
              onClick={() => setRetakeMessages(null)}
            >
              <RefreshCw size={18} />
              Back to camera
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FabricCaptureGuardrailsModal;
